"use client";

import React, { useState, useEffect, useMemo } from "react";
import coverimage from "@/imports/rannbanner.webp";
import Image from "next/image";
import { useAppSelector } from "@/hooks/hooks";
import { ISettings } from "@/types/home.types";
import dynamic from "next/dynamic";

const HeroInquiryForm = dynamic(() => import("./HeroInquiryForm"), {
  ssr: false,
});

export default function HeroFullScreenVideo() {
  const [showVideo, setShowVideo] = useState(false);
  const [shouldLoadIframe, setShouldLoadIframe] = useState(false);
  const [inquiryShow, setInquiryShow] = useState(false);
  const [origin, setOrigin] = useState("");
  const { SettingList, HeaderMenuList } = useAppSelector((state) => state.home);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setOrigin(window.location.origin);
    }
    const showInquiry = setTimeout(() => {
      setInquiryShow(true);
    }, 15000);

    // Skip loading the iframe entirely for search engines and PageSpeed Insights / Lighthouse
    const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
    const isBotOrLighthouse = /lighthouse|chrome-lighthouse|google-pagespeed|googlebot|bingbot|yandex|baidu|duckduckbot/i.test(ua);

    let deferTimer: NodeJS.Timeout;

    const triggerIframeLoad = () => {
      setShouldLoadIframe((prev) => {
        if (prev) return prev;
        return true;
      });
      cleanupListeners();
    };

    const cleanupListeners = () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("scroll", triggerIframeLoad);
        window.removeEventListener("mousemove", triggerIframeLoad);
        window.removeEventListener("touchstart", triggerIframeLoad);
        window.removeEventListener("click", triggerIframeLoad);
      }
    };

    if (isBotOrLighthouse) {
      // For Lighthouse, do not set up timers or interactions to load the heavy iframe
      return () => {
        clearTimeout(showInquiry);
      };
    }

    // Set up listeners for real user interaction
    if (typeof window !== "undefined") {
      window.addEventListener("scroll", triggerIframeLoad, { passive: true });
      window.addEventListener("mousemove", triggerIframeLoad, { passive: true });
      window.addEventListener("touchstart", triggerIframeLoad, { passive: true });
      window.addEventListener("click", triggerIframeLoad, { passive: true });
    }

    // Fallback: load after 5 seconds if no interaction occurs (to show video to passive users)
    deferTimer = setTimeout(triggerIframeLoad, 5000);

    // Listen to messages from the YouTube player to loop the video on end
    const handleMessage = (event: MessageEvent) => {
      if (
        event.origin === "https://www.youtube.com" ||
        event.origin === "https://www.youtube-nocookie.com"
      ) {
        try {
          const data = JSON.parse(event.data);
          if (
            data.event === "infoDelivery" &&
            data.info &&
            data.info.playerState === 0
          ) {
            // Player state 0 is ended, send playVideo command to loop
            const iframe = document.getElementById("widget-fullscreen") as HTMLIFrameElement | null;
            if (iframe && iframe.contentWindow) {
              iframe.contentWindow.postMessage(
                JSON.stringify({ event: "command", func: "playVideo", args: [] }),
                "*"
              );
            }
          }
        } catch (e) {
          // Ignore parsing errors
        }
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      clearTimeout(showInquiry);
      if (deferTimer) clearTimeout(deferTimer);
      cleanupListeners();
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  const Settings: Record<string, ISettings> = useMemo(() => {
    return (
      SettingList?.reduce(
        (acc, item) => {
          acc[item?.SettingKey] = item;
          return acc;
        },
        {} as Record<string, ISettings>,
      ) || {}
    );
  }, [SettingList]);

  const BannerBookNow = Settings?.["BannerBookNow"]?.SettingValue;

  const videoSrc = `https://www.youtube-nocookie.com/embed/kaE2TPVbLcY?autoplay=1&mute=1&loop=1&playlist=kaE2TPVbLcY&controls=0&rel=0&playsinline=1&enablejsapi=1&iv_load_policy=3&disablekb=1&fs=0&modestbranding=1${
    origin ? `&origin=${encodeURIComponent(origin)}` : ""
  }`;

  return (
    <div
      className={`relative w-full bg-black overflow-hidden select-none ${
        inquiryShow ? "min-h-[145vh] md:min-h-0 md:h-[92vh]" : "h-screen md:h-[92vh]"
      }`}
    >
      {shouldLoadIframe && (
        <iframe
          role="presentation"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          title="કચ્છ રણોત્સવ | Rann Utsav - The Tent City | White Desert of India | Rann of Kutch"
          src={videoSrc}
          id="widget-fullscreen"
          className="absolute top-1/2 left-1/2 w-auto h-auto min-w-full min-h-full aspect-video -translate-x-1/2 -translate-y-1/2 pointer-events-none scale-[1.15] border-0"
          referrerPolicy="strict-origin-when-cross-origin"
          onLoad={() => {
            setTimeout(() => {
              setShowVideo(true);
            }, 1000);
          }}
        ></iframe>
      )}

      {/* Cover image overlay to hide initial loading/splash controls and transition smoothly */}
      <Image
        src={coverimage}
        alt="hero banner"
        fill
        priority
        fetchPriority="high"
        className={`object-cover transition-opacity duration-1000 ${
          showVideo ? "opacity-0" : "opacity-100"
        }`}
      />

      {/* Foreground Form Wrapper */}
      {inquiryShow && (
        <HeroInquiryForm
          HeaderMenuList={HeaderMenuList}
          BannerBookNow={BannerBookNow}
        />
      )}
    </div>
  );
}
